
import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { debug } from '@/utils/logger';
import { parseLocalDate, formatDateToYYYYMMDD } from '@/utils/dateUtils';
import { readSavedStaySearch, writeSavedStaySearch } from '@/utils/booking/savedStaySearch';
import './PlaceModalBookingWidget.css';

export interface PlaceModalSearchParams {
    checkIn: string;
    checkOut: string;
    adults: number;
    rooms: number;
    /** Mixpanel only — stripped before whitelabel/API booking. */
    button_action?: 'book' | 'check_availability';
}

interface PlaceModalBookingWidgetProps {
  onParamsChange?: (params: PlaceModalSearchParams | null) => void;
  initialCheckIn?: string | null;
  initialCheckOut?: string | null;
  initialAdults?: number;
  initialRooms?: number;
}

export interface PlaceModalBookingWidgetRef {
  getParams: () => PlaceModalSearchParams | null;
}

/** Same defaults as the widget’s initial state — for compact cards that open whitelabel without showing the widget. */
export function getDefaultPlaceModalBookingParams(): PlaceModalSearchParams {
  const saved = readSavedStaySearch();
  if (saved) {
    return saved;
  }
  const checkIn = dayjs().add(30, 'day').toDate();
  const checkOut = dayjs().add(32, 'day').toDate();
  return {
    checkIn: formatDateToYYYYMMDD(checkIn),
    checkOut: formatDateToYYYYMMDD(checkOut),
    adults: 2,
    rooms: 1,
  };
}

const PlaceModalBookingWidget = forwardRef<PlaceModalBookingWidgetRef, PlaceModalBookingWidgetProps>(({
  onParamsChange,
  initialCheckIn,
  initialCheckOut,
  initialAdults,
  initialRooms,
}, ref) => {
  // Mobile Figma "Number of guests": filled circle steppers
  const isMobile = useIsMobileViewport();
  const DecreaseIcon = isMobile ? RemoveCircleIcon : RemoveIcon;
  const IncreaseIcon = isMobile ? AddCircleIcon : AddIcon;
  // Last search the user picked on this device; ignored when explicit initial values are passed
  const [savedSearch] = useState(() =>
    initialCheckIn || initialCheckOut ? null : readSavedStaySearch(),
  );

  // Set default dates: saved search, else check-in 30 days from now, check-out 2 nights later
  const [checkIn, setCheckIn] = useState<Dayjs | null>(() => {
    if (initialCheckIn) {
      const localDate = parseLocalDate(initialCheckIn);
      return localDate ? dayjs(localDate) : null;
    }
    if (savedSearch) {
      return dayjs(parseLocalDate(savedSearch.checkIn));
    }
    return dayjs().add(30, 'day');
  });
  
  const [checkOut, setCheckOut] = useState<Dayjs | null>(() => {
    if (initialCheckOut) {
      const localDate = parseLocalDate(initialCheckOut);
      return localDate ? dayjs(localDate) : null;
    }
    if (savedSearch) {
      return dayjs(parseLocalDate(savedSearch.checkOut));
    }
    return dayjs().add(32, 'day');
  });
  
  const [adults, setAdults] = useState(initialAdults ?? savedSearch?.adults ?? 2);
  const [rooms, _setRooms] = useState(initialRooms ?? savedSearch?.rooms ?? 1);
  // Only persist after the user changes something, so untouched defaults never get saved
  const hasUserEditedRef = useRef(false);
  const [isCheckInOpen, setIsCheckInOpen] = useState(false);
  const [isCheckOutOpen, setIsCheckOutOpen] = useState(false);

  // Get current params
  const getParams = useCallback((): PlaceModalSearchParams | null => {
    if (!checkIn || !checkOut) {
      return null;
    }
    
    const checkInDate = checkIn.toDate();
    const checkOutDate = checkOut.toDate();
    
    return {
      checkIn: formatDateToYYYYMMDD(checkInDate),
      checkOut: formatDateToYYYYMMDD(checkOutDate),
      adults,
      rooms,
    };
  }, [checkIn, checkOut, adults, rooms]);

  // Expose getParams to parent via ref
  useImperativeHandle(ref, () => ({
    getParams,
  }), [getParams]);

  // Notify parent when params change
  useEffect(() => {
    if (onParamsChange) {
      const params = getParams();
      debug('[PlaceModalBookingWidget] Params changed', params);
      onParamsChange(params);
    }
  }, [checkIn, checkOut, adults, rooms, onParamsChange, getParams]);

  // Remember the user's choice for the next stay they open
  useEffect(() => {
    if (!hasUserEditedRef.current) {
      return;
    }
    const params = getParams();
    if (params) {
      writeSavedStaySearch(params);
    }
  }, [getParams]);

  // Format date for display
  const formatDisplayDate = (date: Dayjs | null): string => {
    if (!date) return 'Select';
    return date.format('MMM D');
  };

  const checkInInputRef = useRef<HTMLInputElement>(null);
  const checkOutInputRef = useRef<HTMLInputElement>(null);

  const handleCheckInCellClick = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setIsCheckOutOpen(false);
    setIsCheckInOpen(true);
  }, []);

  const handleCheckOutCellClick = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setIsCheckInOpen(false);
    setIsCheckOutOpen(true);
  }, []);

  return (
    <div className="place-modal-booking-widget">
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {/* Dates row */}
        <div className="place-modal-booking-dates">
          <div
            className="place-modal-booking-date-field"
            role="button"
            tabIndex={0}
            onClick={(e) => handleCheckInCellClick(e)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCheckInCellClick();
              }
            }}
          >
            <span className="place-modal-booking-label">Check In</span>
            <DatePicker
              value={checkIn}
              open={isCheckInOpen}
              onOpen={() => setIsCheckInOpen(true)}
              onClose={() => setIsCheckInOpen(false)}
              onChange={(date) => {
                hasUserEditedRef.current = true;
                if (date) {
                  const localDate = parseLocalDate(date.toDate());
                  const newCheckIn = localDate ? dayjs(localDate) : null;
                  setCheckIn(newCheckIn);
                  if (newCheckIn && (!checkOut || !checkOut.isAfter(newCheckIn))) {
                    setCheckOut(newCheckIn.add(1, 'day'));
                  }
                  // Close after a brief delay to ensure state updates complete
                  setTimeout(() => setIsCheckInOpen(false), 0);
                } else {
                  setCheckIn(null);
                }
              }}
              minDate={dayjs()}
              slotProps={{
                textField: {
                  variant: 'standard',
                  inputRef: checkInInputRef,
                  InputProps: {
                    disableUnderline: true,
                    className: 'place-modal-booking-date-input',
                  },
                  inputProps: {
                    readOnly: true,
                  },
                  sx: {
                    '& input': {
                      padding: 0,
                      fontSize: '15px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    },
                  },
                },
                popper: {
                  style: { zIndex: 10000001 },
                  placement: 'bottom-start',
                },
                dialog: {
                  style: { zIndex: 10000001 },
                },
                actionBar: {
                  actions: [],
                },
              }}
            />
            <span className="place-modal-booking-date-display">{formatDisplayDate(checkIn)}</span>
          </div>

          <div className="place-modal-booking-date-divider" />

          <div
            className="place-modal-booking-date-field"
            role="button"
            tabIndex={0}
            onClick={(e) => handleCheckOutCellClick(e)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCheckOutCellClick();
              }
            }}
          >
            <span className="place-modal-booking-label">Check Out</span>
            <DatePicker
              value={checkOut}
              open={isCheckOutOpen}
              onOpen={() => setIsCheckOutOpen(true)}
              onClose={() => setIsCheckOutOpen(false)}
              onChange={(date) => {
                hasUserEditedRef.current = true;
                if (date) {
                  const localDate = parseLocalDate(date.toDate());
                  setCheckOut(localDate ? dayjs(localDate) : null);
                  // Close after a brief delay to ensure state updates complete
                  setTimeout(() => setIsCheckOutOpen(false), 0);
                } else {
                  setCheckOut(null);
                }
              }}
              minDate={checkIn ? checkIn.add(1, 'day') : dayjs()}
              slotProps={{
                textField: {
                  variant: 'standard',
                  inputRef: checkOutInputRef,
                  InputProps: {
                    disableUnderline: true,
                    className: 'place-modal-booking-date-input',
                  },
                  inputProps: {
                    readOnly: true,
                  },
                  sx: {
                    '& input': {
                      padding: 0,
                      fontSize: '15px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    },
                  },
                },
                popper: {
                  style: { zIndex: 10000001 },
                  placement: 'bottom-start',
                },
                dialog: {
                  style: { zIndex: 10000001 },
                },
                actionBar: {
                  actions: [],
                },
              }}
            />
            <span className="place-modal-booking-date-display">{formatDisplayDate(checkOut)}</span>
          </div>
        </div>

        {/* Guests row */}
        <div className="place-modal-booking-guests">
          <button
            type="button"
            className="place-modal-booking-guest-btn"
            onClick={() => {
              hasUserEditedRef.current = true;
              setAdults(Math.max(1, adults - 1));
            }}
            disabled={adults <= 1}
            aria-label="Decrease adults"
          >
            <DecreaseIcon sx={{ fontSize: 24 }} />
          </button>
          <span className="place-modal-booking-guest-count">
            {adults} Adult{adults !== 1 ? 's' : ''}
          </span>
          <button
            type="button"
            className="place-modal-booking-guest-btn"
            onClick={() => {
              hasUserEditedRef.current = true;
              setAdults(adults + 1);
            }}
            aria-label="Increase adults"
          >
            <IncreaseIcon sx={{ fontSize: 24 }} />
          </button>
        </div>
      </LocalizationProvider>
    </div>
  );
});

PlaceModalBookingWidget.displayName = 'PlaceModalBookingWidget';

export default PlaceModalBookingWidget;
